import { MIME_TYPES_MAP, ROLES, ROLES_CAP } from './Constants';

/**
 * Normaliza el texto de búsqueda cuando parece un CUIT (solo dígitos, guiones, espacios):
 * quita guiones y espacios para que "33-65254615-9" y "33652546159" se traten igual en todas las búsquedas.
 * Si el texto contiene letras (ej. razón social), se devuelve tal cual.
 */
export const normalizeCuitForSearch = (text: string): string => {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (/^[\d\s-]+$/.test(trimmed)) {
    return trimmed.replace(/[\s-]/g, '');
  }
  return trimmed;
};

export const capitalize = (word: string) => {
  const lower = word.toLowerCase();
  return word.charAt(0).toUpperCase() + lower.slice(1);
};

export const removeAccents = (word: string) => {
  const lower = word.toLowerCase();
  return word.charAt(0).toUpperCase() + lower.slice(1);
};

export function isSuccessfulResponse(
  response: any,
  expectedStatuses: number[] = [200, 201, 204]
): boolean {
  return (
    response &&
    typeof response.status === 'number' &&
    expectedStatuses.includes(response.status)
  );
}

export function hasError(response: any): {
  isClientError: boolean;
  isTechError: boolean;
  isAnyError: boolean;
} {
  if (!response || typeof response.status !== 'number') {
    return { isClientError: false, isTechError: false, isAnyError: false };
  }

  const isClientError = response.status >= 400 && response.status < 500;
  const isTechError = response.status >= 500;
  const isAnyError = isClientError || isTechError;

  return { isClientError, isTechError, isAnyError };
}

export function hasData<T>(response: any): response is { data: T } {
  return response && 'data' in response;
}

export function hasContent<T>(
  response: any
): response is { data: { content: T } } {
  return (
    Boolean(response) &&
    response.success !== false &&
    response.data != null &&
    Array.isArray(response.data.content)
  );
}

/** Respuesta paginada de GET /guarantees/overdue (snake_case vía BFF). */
export function getOverduePageData(response: any): {
  content: any[];
  size: number;
  page: number;
  total_elements: number;
  total_pages: number;
} | null {
  if (!response || response.success === false) return null;
  const d = response.data;
  if (!d || typeof d !== 'object') return null;
  const content = d.content;
  if (!Array.isArray(content)) return null;
  const size = Number(d.size);
  const page = Number(d.page);
  const total_elements = Number(d.total_elements ?? d.totalElements);
  const total_pages = Number(d.total_pages ?? d.totalPages);
  return {
    content,
    size: Number.isFinite(size) ? size : 10,
    page: Number.isFinite(page) ? page : 1,
    total_elements: Number.isFinite(total_elements) ? total_elements : 0,
    total_pages: Number.isFinite(total_pages) ? total_pages : 0,
  };
}

export const isEmptyValue = (value: any): boolean => {
  return value === '' || value == null || Number.isNaN(value);
};

export const getRolesFromToken = (): string[] => {
  const token = localStorage.getItem('roles');
  return token ? token.split(',') : [];
};

/** Prioridad alineada con useRole; etiquetas granulares para roles de comité. */
const PRIMARY_ROLE_DISPLAY_ENTRIES: ReadonlyArray<readonly [string, string]> = [
  [ROLES_CAP.admin, ROLES.admin],
  [ROLES_CAP.producer, ROLES.producer],
  [ROLES_CAP.commercial, ROLES.commercial],
  [ROLES_CAP.riskAnalyst, ROLES.riskAnalyst],
  [ROLES_CAP.instrumentator, ROLES.instrumentator],
  [ROLES_CAP.comite, ROLES.comite],
  [ROLES_CAP.jcyc, ROLES.jcyc],
  [ROLES_CAP.jah, ROLES.jah],
  [ROLES_CAP.jcc, ROLES.jcc],
  [ROLES_CAP.jch, ROLES.jch],
  [ROLES_CAP.ggdhe, ROLES.ggdhe],
  [ROLES_CAP.gf, ROLES.gf],
  [ROLES_CAP.gm, ROLES.gm],
  [ROLES_CAP.gi, ROLES.gi],
  [ROLES_CAP.dn, ROLES.dn],
  [ROLES_CAP.dnh, ROLES.dnh],
  [ROLES_CAP.daf, ROLES.daf],
  [ROLES_CAP.gg, ROLES.gg],
  [ROLES_CAP.cd, ROLES.cd],
  [ROLES_CAP.uyJefeCc, ROLES.uyJefeCc],
  [ROLES_CAP.uyhoJefeAd, ROLES.uyhoJefeAd],
  [ROLES_CAP.uyComiteGcia, ROLES.uyComiteGcia],
  [ROLES_CAP.uyComiteGciaCom, ROLES.uyComiteGciaCom],
  [ROLES_CAP.uyhoComiteCl, ROLES.uyhoComiteCl],
  [ROLES_CAP.uyComiteCa, ROLES.uyComiteCa],
  [ROLES_CAP.uyDirectorGg, ROLES.uyDirectorGg],
  [ROLES_CAP.uyceComiteCl, ROLES.uyceComiteCl],
];

export const getPrimaryRoleDisplayLabel = (userRoles: string[]): string => {
  for (const [code, label] of PRIMARY_ROLE_DISPLAY_ENTRIES) {
    if (userRoles.includes(code)) {
      return label;
    }
  }
  return '';
};

export const hasRole = (roleCode: string): boolean => {
  return getRolesFromToken().includes(roleCode);
};

export const hasAnyRole = (roleCodes: string[]): boolean => {
  const userRoles = getRolesFromToken();
  return roleCodes.some(code => userRoles.includes(code));
};

export const removeFileExtension = (filename: string) => {
  return filename.substring(0, filename.lastIndexOf('.')) || filename;
};

export const toNumber = (val: number | string | undefined | null): number => {
  if (val === undefined || val === null) return 0;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const dotCount = (val.match(/\./g) || []).length;
    if (!val.includes(',') && dotCount === 1 && !/\.\d{3}$/.test(val)) {
      return parseFloat(val) || 0;
    }
    const num = parseFloat(val.replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

/** Campos de resultado bruto usados para calcular el total (paso 2 balance / payload para origination y EBIT). Valores típicos: string (formato formulario) o number. */
export type GrossResultInput = {
  agricultural_production?: string | number;
  production_cost?: string | number;
  sales?: string | number;
  cost_of_sales?: string | number;
};

/**
 * Calcula Resultado del ejercicio con la misma fórmula del paso 2 (balance).
 * Usado al armar el payload de guardado para que origination y BPB_004/BPB_019 lo usen.
 */
export function computeResultForTheYear(
  resultBeforeTaxesTotal: number | string | undefined | null,
  incomeTax: number | string | undefined | null
): number {
  return Math.round((toNumber(resultBeforeTaxesTotal) + toNumber(incomeTax)) * 100) / 100;
}

/**
 * Calcula el resultado bruto total con la misma fórmula del paso 2 (balance).
 * Usado al armar el payload de guardado para que origination y el indicador EBIT lo usen.
 */
export function computeGrossResultTotal(grossResult: GrossResultInput | undefined): number {
  if (!grossResult) return 0;
  return (
    Math.round(
      (toNumber(grossResult.agricultural_production) -
        toNumber(grossResult.production_cost) +
        toNumber(grossResult.sales) -
        toNumber(grossResult.cost_of_sales)) *
        100
    ) / 100
  );
}

/** Componentes del balance (activo/pasivo) usados para calcular totales en paso 2 y payload para origination (total_assets_amount, BPB_024, etc.). */
export type BalanceSheetPayloadTotalsInput = {
  current_assets?: Record<string, unknown> | null;
  non_current_assets?: Record<string, unknown> | null;
  current_liabilities?: Record<string, unknown> | null;
  non_current_liabilities?: Record<string, unknown> | null;
};

/**
 * Suma los valores numéricos de un componente del balance (ej. current_assets), excluyendo la clave 'total'.
 * Misma fórmula usada en BalanceLoader y DualBalanceLoader para el payload de guardado.
 */
export function sumBalanceSheetComponentValues(
  obj: Record<string, unknown> | null | undefined
): number {
  if (!obj || typeof obj !== 'object') return 0;
  return Object.entries(obj)
    .filter(([k]) => k !== 'total')
    .reduce((s, [, v]) => s + toNumber(v as string | number), 0);
}

/**
 * Calcula los totales de activos y pasivos para el payload de guardado del balance (paso 2).
 * Usado por BalanceLoader y DualBalanceLoader para que origination persista total_assets_amount y total_liabilities_amount.
 */
export function computeBalanceSheetPayloadTotals(
  data: BalanceSheetPayloadTotalsInput
): {
  currentAssetsTotal: number;
  nonCurrentAssetsTotal: number;
  totalAssetsAmount: number;
  totalLiabilitiesAmount: number;
} {
  const currentAssetsTotal = sumBalanceSheetComponentValues(data.current_assets ?? undefined);
  const nonCurrentAssetsTotal = sumBalanceSheetComponentValues(data.non_current_assets ?? undefined);
  const totalAssetsAmount = currentAssetsTotal + nonCurrentAssetsTotal;
  const totalLiabilitiesAmount =
    sumBalanceSheetComponentValues(data.current_liabilities ?? undefined) +
    sumBalanceSheetComponentValues(data.non_current_liabilities ?? undefined);
  return {
    currentAssetsTotal,
    nonCurrentAssetsTotal,
    totalAssetsAmount,
    totalLiabilitiesAmount,
  };
}

export const getMimeType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';
  return MIME_TYPES_MAP[extension] || 'application/octet-stream';
};

export const getEnabledRiskCodes = (codes: string[]): string[] => {
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isCasfogEnabled = config?.isCasfogEnabled ?? true;
  
  if (isCasfogEnabled) {
    return codes;
  }
  
  return codes.filter(code => code !== 'CASFOG');
};
